import React, { useState, useEffect } from "react";
import "./DirectoryTree.css";
import folder_icon from "../public/img/folder_icon.svg";
import folder_open_icon from "../public/img/folder_open_icon.svg";
import downCaret from "../public/img/down_caret.svg";

const DirectoryTree = ({
  chatbots,
  selectedDirectoryId,
  onSelectDirectory,
  onClose,
  expandedDirectories = {},
}) => {
  const [expanded, setExpanded] = useState(expandedDirectories);
  const [clickTimeout, setClickTimeout] = useState(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  // Helper to check if an icon is an SVG string
  const isSvgContent = (str) => {
    if (!str) return false;
    return typeof str === "string" && str.trim().startsWith("<");
  };

  // Function to toggle expanded state of a directory
  const toggleDirectory = (directoryId) => {
    setExpanded((prev) => ({
      ...prev,
      [directoryId]: !prev[directoryId],
    }));
  };

  // Function to organize chatbots into a hierarchical structure
  const organizeDirectories = (items) => {
    // Only include directories
    const directories = items.filter((item) => item.directory === true);

    // Create a map for quick lookup
    const directoryMap = {};
    directories.forEach((dir) => {
      directoryMap[dir._id] = { ...dir, children: [] };
    });

    // Organize into a tree structure
    const rootDirectories = [];

    directories.forEach((dir) => {
      const directoryWithChildren = directoryMap[dir._id];

      // If it has a parent and the parent exists in our map
      if (dir.parent && directoryMap[dir.parent]) {
        directoryMap[dir.parent].children.push(directoryWithChildren);
      } else {
        // This is a root directory
        rootDirectories.push(directoryWithChildren);
      }
    });

    return rootDirectories;
  };

  // Handle single vs double click
  const handleDirectoryClick = (directoryId, hasChildren) => {
    // Clear existing timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      // This is a double click
      onSelectDirectory(directoryId);
      if (onClose) onClose();
      return;
    }

    // Set timeout for single click
    const timeout = setTimeout(() => {
      // Single click: select and expand if has children
      onSelectDirectory(directoryId);
      if (hasChildren) {
        setExpanded((prev) => ({
          ...prev,
          [directoryId]: true,
        }));
      }
      setClickTimeout(null);
    }, 250);
    
    setClickTimeout(timeout);
  };

  // Recursively render directory and its children
  const renderDirectory = (directory, level = 0) => {
    const isExpanded = expanded[directory._id];
    const isSelected = selectedDirectoryId === directory._id;
    const hasChildren = directory.children && directory.children.length > 0;

    return (
      <div
        key={directory._id}
        className="directory-tree-item"
        style={{ paddingLeft: `${level * 20}px` }}
      >
        <div
          className={`directory-tree-node ${isSelected ? "selected" : ""}`}
          onClick={() => handleDirectoryClick(directory._id, hasChildren)}
        >
          <div
            className="directory-tree-toggle"
            onClick={(e) => {
              e.stopPropagation();
              toggleDirectory(directory._id);
            }}
          >
            {directory.children && directory.children.length > 0 ? (
              <img
                src={downCaret}
                alt="toggle"
                className={`dir-tree-caret ${isExpanded ? "open" : ""}`}
              />
            ) : (
              <span className="directory-tree-spacer"></span>
            )}
          </div>

          <div className="directory-tree-icon">
            {directory.icon && isSvgContent(directory.icon) ? (
              <div
                className="directory-svg-container"
                dangerouslySetInnerHTML={{ __html: directory.icon }}
              />
            ) : (
              <img
                src={isExpanded ? folder_open_icon : folder_icon}
                alt="Folder"
                className="directory-icon"
              />
            )}
          </div>

          <div className="directory-tree-name">{directory.title}</div>
        </div>

        {isExpanded && directory.children && directory.children.length > 0 && (
          <div className="directory-tree-children">
            {directory.children.map((child) =>
              renderDirectory(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  // Organize directories into a tree
  const directoryTree = organizeDirectories(chatbots);

  return (
    <div className="directory-tree-container">
      <div
        className={`directory-tree-node root-node ${
          selectedDirectoryId === "" ? "selected" : ""
        }`}
        onClick={() => handleDirectoryClick("", false)}
      >
        <div className="directory-tree-toggle">
          <span className="directory-tree-spacer"></span>
        </div>
        <div className="directory-tree-icon">
          <img src={folder_icon} alt="Root" className="directory-icon" />
        </div>
        <div className="directory-tree-name">Root (No Parent)</div>
      </div>

      {directoryTree.map((directory) => renderDirectory(directory))}
    </div>
  );
};

export default DirectoryTree;
